import { useEffect, useMemo, useRef } from 'react'
import { IEquipment, IEquipmentEvent } from '../equipment.types'
import useErrorEventsRef from './useErrorEventsRef'

export interface IUseLastErrorEventProps {
  equipmentId: IEquipment['id']

  tsFrom: number
  tsTo: number
  tsCurrent: number
  onNewValueReceived: () => void
}

const errorEventLifeTimeMs = 3000

const useErrorEventUpToTsRef = (props: IUseLastErrorEventProps) => {
  const lastUpdateTsRef = useRef(0)
  const lastErrorEventsRef = useRef<IEquipmentEvent[]>([])

  const config = useMemo(
    () => ({
      equipmentId: props.equipmentId,
      tsFrom: props.tsFrom,
      tsTo: props.tsTo,
      eventTimeMarginSeconds: errorEventLifeTimeMs / 1000,
    }),
    [props.equipmentId, props.tsFrom, props.tsTo]
  )

  const eventsData = useErrorEventsRef(config)

  const errorEventsByTime = useMemo(() => {
    if (eventsData.loading) return {}

    const res = {}
    for (const event of eventsData.eventsRef.current) {
      const key = new Date(event.createdAt).getTime()
      res[key] = event
    }

    return res
  }, [eventsData.loading])

  useEffect(() => {
    if (!errorEventsByTime) return
    lastErrorEventsRef.current = []

    for (const ts in errorEventsByTime) {
      if (Math.abs(Number(ts) - props.tsCurrent) <= errorEventLifeTimeMs) {
        lastErrorEventsRef.current.push(errorEventsByTime[ts])
      }
    }

    lastUpdateTsRef.current = props.tsCurrent
    props.onNewValueReceived()
  }, [props.onNewValueReceived, props.tsCurrent, errorEventsByTime])

  return { ref: lastErrorEventsRef, loading: eventsData.loading, lastUpdateTsRef }
}

export default useErrorEventUpToTsRef
